import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const createInvitation = async (data) => {
  try {
    const response = await api.post('/company/affiliates/invite', data);
    return response.data;
  } catch (error) {
    throw error;
    // throw error.response?.data || { message: 'Failed to create invitation' };
  }
}; 

export const getInvitation = async (token) => {
  try {
    const response = await api.get(`/company/invitations/${token}`);
    return response.data;
  } catch (error) {
    // throw error.response?.data || { message: 'Failed to get invitation' };
    throw error;
  }
};

export const acceptInvitation = async (token) => {
  try {
    const response = await api.post(`/company/invitations/${token}/accept`);
    console.log('Invitation accepted response:', response.data);
    return {
      ...response.data,
      userId: response.data.userId
    };
  } catch (error) {
    throw error.response?.data || { message: 'Failed to accept invitation' };
  }
};

export const rejectInvitation = async (token) => {
  try {
    const response = await api.post(`/company/invitations/${token}/reject`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to reject invitation' };
  }
};

export const acceptCampaign = async (token, campaignId) => {
  try {
    const response = await api.post(`/company/invitations/${token}/accept/${campaignId}`);
    console.log('Campaign accepted response:', response.data);
    return {
      ...response.data,
      userId: response.data.invitation?.userId
    };
  } catch (error) {
    throw error.response?.data || { message: 'Failed to accept campaign' };
  }
};

export const rejectCampaign = async (token, campaignId) => {
  try {
    const response = await api.post(`/company/invitations/${token}/reject/${campaignId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to reject campaign' };
  }
};

export const getCampaignNegotiationThread = async (token, campaignId) => {
  try {
    const response = await api.get(
      `/company/invitations/${token}/campaigns/${campaignId}/negotiation/thread`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to load negotiation thread' };
  }
};

export const sendCampaignCounterOffer = async (token, campaignId, { amountInr, text }) => {
  try {
    const response = await api.post(
      `/company/invitations/${token}/campaigns/${campaignId}/negotiation/message`,
      { amountInr, text }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to send counter-offer' };
  }
};

export const acceptCampaignOffer = async (token, campaignId, { agreedRateInr }) => {
  try {
    const response = await api.post(
      `/company/invitations/${token}/campaigns/${campaignId}/negotiation/accept`,
      { agreedRateInr }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to accept offer' };
  }
};

export const signCampaignContract = async (token, campaignId, { signedBy }) => {
  try {
    const response = await api.post(
      `/company/invitations/${token}/campaigns/${campaignId}/contract/sign`,
      { signedBy }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to sign contract' };
  }
};

export const confirmCampaignContract = async (token, campaignId) => {
  try {
    const response = await api.post(
      `/company/invitations/${token}/campaigns/${campaignId}/contract/confirm`,
      {}
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to confirm campaign' };
  }
};

export const getAffiliateInvitationsByUsername = async (username, params = {}) => {
  try {
    const clean = String(username || "").trim().replace(/^@/, "");
    const response = await api.get(`/company/affiliates/username/${encodeURIComponent(clean)}`, { params });
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.message || "Failed to get affiliate invitations");
  } catch (error) {
    throw error.response?.data || { message: "Failed to get affiliate invitations" };
  }
};

// get affiliate earnign by the user id
export const getAffiliateEarnings = async (userId) => {
  try {
    // console.log('Fetching earnings for userId:', userId);
    const response = await api.get(`/company/user/${userId}/earnings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching earnings:', error);
    throw error.response?.data || { message: 'Failed to fetch earnings' };
  }
};

export const getAvailableCampaigns = async (userId) => {
  try {
    const response = await api.get(`/company/affiliates/available-campaigns/${userId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to get available campaigns' };
  }
};

export const addCampaignsToAffiliate = async (userId, campaignIds) => {
  try {
    const response = await api.post(`/company/affiliates/add-campaigns/${userId}`, { campaigns: campaignIds });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to add campaigns' };
  }
};

export const getMarketplaceUsers = async (campaignId = null) => {
  try {
    const response = await api.get('/auth/users/marketplace', {
      params: { campaignId }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch marketplace users' };
  }
};

export const sendInvitationsToAffiliates = async (userIds, campaignId, expiresAt) => {
  try {
    const response = await api.post('/company/affiliates/invite-multiple', { 
      userIds,
      campaignId,
      expiresAt
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to send invitations' };
  }
};

export const getAvailableUsersForCampaign = async (campaignId) => {
  try {
    const response = await api.get(`/company/campaigns/${campaignId}/available-users`);
    return response.data;
  } catch (error) {
    console.error('Error fetching available users for campaign:', error);
    throw error;
  }
};

export const addInstagramMedia = async (postLinks, campaignId = null) => {
  try {
    const response = await api.post('/auth/instagram/add', { 
      postLinks,
      campaignId 
    }, { withCredentials: true });
    
    // If backend returns success: false, throw it so it goes to catch block
    if (!response.data.success) {
      const error = new Error(response.data.message || 'Failed to add Instagram media');
      error.response = { data: response.data };
      throw error;
    }
    return response.data;
  } catch (error) {
    console.error('Error adding Instagram media:', error);
    
    // Handle Instagram session expiration specifically
    if (error.response?.data?.requiresReconnection || 
        error.response?.data?.error === 'instagram_session_expired' ||
        error.response?.data?.error === 'instagram_unauthorized') {
      const reconnectionError = new Error('Instagram session expired. Please reconnect your Instagram account.');
      reconnectionError.response = { data: error.response.data };
      reconnectionError.requiresReconnection = true;
      throw reconnectionError;
    }

    // Handle Instagram permission errors (Basic Display API limitations)
    if (error.response?.data?.error === 'instagram_permission_denied') {
      const permissionError = new Error('This post is not accessible with your current Instagram permissions. Please ensure you are adding your own posts.');
      permissionError.response = { data: error.response.data };
      permissionError.permissionDenied = true;
      throw permissionError;
    }

    // Handle no posts found error
    if (error.response?.data?.error === 'no_posts_found') {
      const noPostsError = new Error('No Instagram posts could be found. Please ensure you are adding your own posts and they are publicly accessible.');
      noPostsError.response = { data: error.response.data };
      noPostsError.noPostsFound = true;
      throw noPostsError;
    }
    
    throw error.response?.data || { message: 'Failed to add Instagram media' };
  }
};

export const addYouTubeMedia = async (videoLinks, campaignId = null) => {
  try {
    const response = await api.post('/auth/youtube/add', { 
      videoLinks,
      campaignId 
    }, { withCredentials: true });
    
    // If backend returns success: false, throw it so it goes to catch block
    if (!response.data.success) {
      const error = new Error(response.data.message || 'Failed to add media');
      error.response = { data: response.data };
      throw error;
    }
    return response.data;
  } catch (error) {
    console.error('Error adding YouTube media:', error);
    
    // Handle YouTube session expiration specifically
    if (error.response?.data?.requiresReconnection || 
        error.response?.data?.error === 'youtube_session_expired' ||
        error.response?.data?.error === 'youtube_unauthorized') {
      const reconnectionError = new Error('YouTube session expired. Please reconnect your YouTube account.');
      reconnectionError.response = { data: error.response.data };
      reconnectionError.requiresReconnection = true;
      throw reconnectionError;
    }
    
    throw error.response?.data || { message: 'Failed to add YouTube media' };
  }
};

export const getUserYouTubeMedia = async () => {
  const response = await api.get('/auth/youtube/media');
  return response.data;
};

// New functions for YouTube media approval
export const getYouTubeMediaApprovalRequests = async () => {
  try {
    // Call the brands service which will internally call the auth service
    const response = await api.get('/company/youtube/approval-requests');
    return response.data;
  } catch (error) {
    console.error('Error fetching YouTube media approval requests:', error);
    throw error.response?.data || { message: 'Failed to fetch approval requests' };
  }
};

export const approveYouTubeMedia = async (mediaId, videoId, campaignId, action, rejectionReason = '') => {
  try {
    // Call the brands service which will internally call the auth service
    const response = await api.post('/company/youtube/approve', {
      mediaId,
      videoId,
      campaignId,
      action,
      rejectionReason: action === 'reject' ? rejectionReason : undefined
    });
    return response.data;
  } catch (error) {
    console.error('Error approving YouTube media:', error);
    throw error.response?.data || { message: 'Failed to approve YouTube media' };
  }
};

export const getCampaignYouTubeMedia = async (campaignId, affiliateId) => {
  try {
    const response = await api.get(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/youtube-media`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign YouTube media:', error);
    throw error.response?.data || { message: 'Failed to fetch campaign YouTube media' };
  }
};

// Instagram media approval functions
export const getCampaignInstagramMedia = async (campaignId, affiliateId) => {
  try {
    const response = await api.get(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/instagram-media`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign Instagram media:', error);
    throw error.response?.data || { message: 'Failed to fetch campaign Instagram media' };
  }
};

export const getInstagramMediaApprovalRequests = async () => {
  try {
    // Call the brands service which will internally call the auth service
    const response = await api.get('/company/instagram/approval-requests');
    return response.data;
  } catch (error) {
    console.error('Error fetching Instagram media approval requests:', error);
    throw error.response?.data || { message: 'Failed to fetch approval requests' };
  }
};

export const approveInstagramMedia = async (mediaId, postId, campaignId, action, rejectionReason = '') => {
  try {
    // Call the brands service which will internally call the auth service
    const response = await api.post('/company/instagram/approve', {
      mediaId,
      postId,
      campaignId,
      action,
      rejectionReason: action === 'reject' ? rejectionReason : undefined
    });
    return response.data;
  } catch (error) {
    console.error('Error approving Instagram media:', error);
    throw error.response?.data || { message: 'Failed to approve Instagram media' };
  }
};

// YouTube tracking and performance functions
export const getYouTubeMediaPerformance = async (mediaId, videoId, campaignId) => {
  try {
    const response = await api.get(`/auth/youtube/media/${mediaId}/video/${videoId}/campaign/${campaignId}/performance`);
    return response.data;
  } catch (error) {
    console.error('Error fetching YouTube media performance:', error);
    throw error.response?.data || { message: 'Failed to fetch performance metrics' };
  }
};

export const getUserTrackingJobs = async () => {
  try {
    const response = await api.get('/auth/youtube/tracking-jobs');
    return response.data;
  } catch (error) {
    console.error('Error fetching tracking jobs:', error);
    throw error.response?.data || { message: 'Failed to fetch tracking jobs' };
  }
};

export const cancelTrackingJob = async (jobId) => {
  try {
    const response = await api.delete(`/auth/youtube/tracking-jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling tracking job:', error);
    throw error.response?.data || { message: 'Failed to cancel tracking job' };
  }
};

export const getJobStatus = async (jobId) => {
  try {
    const response = await api.get(`/auth/youtube/tracking-jobs/${jobId}/status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job status:', error);
    throw error.response?.data || { message: 'Failed to fetch job status' };
  }
};

export const triggerStatsUpdate = async (mediaId, videoId, campaignId) => {
  try {
    const response = await api.post('/auth/youtube/trigger-stats-update', {
      mediaId,
      videoId,
      campaignId
    });
    return response.data;
  } catch (error) {
    console.error('Error triggering stats update:', error);
    throw error.response?.data || { message: 'Failed to trigger stats update' };
  }
};

export const getUserPerformanceMetrics = async () => {
  try {
    const response = await api.get('/auth/youtube/performance-metrics');
    return response.data;
  } catch (error) {
    console.error('Error fetching performance metrics:', error);
    throw error.response?.data || { message: 'Failed to fetch performance metrics' };
  }
};

// Calculate affiliate's own payments across all campaigns
export const calculateAffiliatePayments = async (userId) => {
  try {
    console.log('Calculating payments for userId:', userId);
    const response = await api.get(`/company/user/${userId}/calculate-payments`);
    return response.data;
  } catch (error) {
    console.error('Error calculating affiliate payments:', error);
    throw error.response?.data || { message: 'Failed to calculate payments' };
  }
};

// Get service pricing data for a specific user
export const getUserServicePricing = async (userId) => {
  try {
    const response = await api.get(`/auth/users/service-payment/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user service pricing:', error);
    throw error.response?.data || { message: 'Failed to fetch service pricing' };
  }
};

// Get affiliate financial details for a specific campaign
export const getCampaignAffiliateFinancials = async (campaignId) => {
  try {
    console.log('Fetching financial details for campaign:', campaignId);
    const response = await api.get(`/company/campaigns/${campaignId}/affiliate-financials`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign affiliate financials:', error);
    throw error.response?.data || { message: 'Failed to fetch affiliate financial details' };
  }
};

// Confirm payment receipt by influencer
export const confirmPaymentReceipt = async (paymentId, historyId, isReceived) => {
  try {
    console.log('Confirming payment receipt:', { paymentId, historyId, isReceived });
    const response = await api.post('/company/payment/confirm-receipt', {
      paymentId,
      historyId,
      isReceived
    });
    return response.data;
  } catch (error) {
    console.error('Error confirming payment receipt:', error);
    throw error.response?.data || { message: 'Failed to confirm payment receipt' };
  }
};